/**
 * ONE DATED SCHEDULE ANSWER, CARRIED THE WHOLE WAY.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS
 * ═══════════════════════════════════════════════════════════════════════════
 * FLOYD-7. On Tuesday at 19:06, OptiPlace ranked Joe D — who works Tuesday
 * 07:30–22:00 and was on shift — BELOW technicians who had gone home at four,
 * told him a 53-minute job needed a rollover, and explained it with:
 *
 *     ranked below because they can start earlier (7:30 against 19:07)
 *
 * Three defects, one shape: an answer about a NAMED DATE was computed
 * correctly and then replaced, downstream, by something that had no date.
 *
 *   A. `findEarliestStartTime` resolved Tuesday properly, then `techContext`
 *      stored `tech.shift_start` / `tech.shift_end` — the `typical_week`
 *      MODE, 16:00 for Joe because three 16:00 weekdays outvote two 22:00
 *      ones. Rollover was then decided against 16:00.
 *   B. A technician past their close was handed a next-day start built from
 *      TODAY's open, and marked schedulable for a request that named TODAY.
 *   C. Ranking compared `getHours()*60 + getMinutes()`, so tomorrow 07:30
 *      (450) beat today 19:07 (1147).
 *
 * The repair is not another `getTechDayHours` call at each symptom. It is to
 * resolve the appointment date ONCE, into a structure that REMEMBERS which
 * date it answered for, and to carry that through availability → slot search
 * → rollover → ranking → explanation.
 *
 * The flat `shift_start` / `shift_end` columns stay what they are: a summary
 * of a typical week, fine for a roster card, never an input to a question
 * that names a day.
 */

import {
  resolveTechnicianScheduleState,
  type TechnicianScheduleState,
} from './technicianScheduleState.ts';

/** How far ahead `classifyStartOpportunity` will look for the next working day. */
const FUTURE_SEARCH_DAYS = 14;

/** The hours envelope the canonical resolver reads. */
export interface TechHoursInput {
  hours?: unknown;
  time_off?: string[] | null;
}

/**
 * A schedule answer that knows WHICH DAY it answered for.
 *
 * The date is not decoration. A bare `{ open, close }` is exactly what let a
 * Tuesday answer be applied to a Wednesday question, and what let the weekly
 * mode pass for a day's real close.
 */
export interface DatedShiftEnvelope {
  /** The shop-local date, `yyyy-MM-dd`, this envelope is about. */
  dateStr: string;
  state: TechnicianScheduleState['state'];
  /** Minutes from shop-local midnight, or null when there is no shift. */
  openMinute: number | null;
  closeMinute: number | null;
  /** Present for `not_scheduled` / `hours_unknown`, so callers can say WHY. */
  reason: string | null;
}

/** `HH:MM` → minutes from midnight. Null on anything unparseable. */
function toMinutes(hhmm: string | null | undefined): number | null {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm));
  if (!m) return null;
  const mins = Number(m[1]) * 60 + Number(m[2]);
  return Number.isFinite(mins) ? mins : null;
}

/**
 * Resolve one technician's schedule for one shop-local date.
 *
 * Thin on purpose: `resolveTechnicianScheduleState` already owns weekday
 * resolution, both key shapes and time-off, and having a second opinion about
 * what "off today" means is how these answers drifted apart before.
 */
export function resolveDatedShiftEnvelope(
  tech: TechHoursInput,
  dateStr: string,
): DatedShiftEnvelope {
  const state = resolveTechnicianScheduleState(
    { hours: tech.hours, time_off: tech.time_off ?? [] } as never,
    dateStr,
  );

  if (state.state === 'scheduled') {
    return {
      dateStr,
      state: 'scheduled',
      openMinute: toMinutes(state.open),
      closeMinute: toMinutes(state.close),
      reason: null,
    };
  }

  return {
    dateStr,
    state: state.state,
    openMinute: null,
    closeMinute: null,
    reason: (state as { reason?: string }).reason ?? null,
  };
}

/**
 * When, if ever, can this technician begin — and on WHICH DAY.
 *
 * `same_day` and `future_day` are deliberately different answers. Collapsing
 * them into one `canSchedule = true` is defect B: a request that named today
 * got back a technician who cannot work today.
 */
export type StartOpportunityKind = 'same_day' | 'future_day' | 'none';

export interface StartOpportunity {
  kind: StartOpportunityKind;
  /** The shop-local date the start falls on — NOT necessarily the requested one. */
  startDateStr: string | null;
  /** Minutes from shop-local midnight on `startDateStr`. */
  startMinute: number | null;
  /** The envelope for `startDateStr`, so downstream needs no second lookup. */
  envelope: DatedShiftEnvelope | null;
  /** The envelope for the REQUESTED date, so callers can explain a refusal. */
  requestedEnvelope: DatedShiftEnvelope;
}

/** `yyyy-MM-dd` + n days, in plain calendar arithmetic (no zone involved). */
function addDays(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}

/**
 * @param nowMinuteOnRequestedDate Minutes from midnight if the requested date
 *   IS today, else `null`. A future appointment begins from that day's own
 *   open — clamping it to "now" would answer a question nobody asked.
 */
export function classifyStartOpportunity(
  tech: TechHoursInput,
  requestedDateStr: string,
  nowMinuteOnRequestedDate: number | null,
  _tz: string,
): StartOpportunity {
  const requested = resolveDatedShiftEnvelope(tech, requestedDateStr);

  if (requested.state === 'scheduled' && requested.closeMinute !== null) {
    // The earliest this person could begin today: their open, or the next
    // minute if the day is already under way.
    const earliest = nowMinuteOnRequestedDate === null
      ? requested.openMinute!
      : Math.max(requested.openMinute!, nowMinuteOnRequestedDate + 1);

    if (earliest < requested.closeMinute) {
      return {
        kind: 'same_day',
        startDateStr: requestedDateStr,
        startMinute: earliest,
        envelope: requested,
        requestedEnvelope: requested,
      };
    }
    // Past the close. NOT a same-day candidate — fall through and look for a
    // real future day rather than inventing one from today's hours.
  }

  // The next day this person actually works, with THAT day's own hours.
  // Days off and time off are skipped, not filled in.
  for (let i = 1; i <= FUTURE_SEARCH_DAYS; i += 1) {
    const dateStr = addDays(requestedDateStr, i);
    const env = resolveDatedShiftEnvelope(tech, dateStr);
    if (env.state === 'scheduled' && env.openMinute !== null) {
      return {
        kind: 'future_day',
        startDateStr: dateStr,
        startMinute: env.openMinute,
        envelope: env,
        requestedEnvelope: requested,
      };
    }
  }

  return {
    kind: 'none',
    startDateStr: null,
    startMinute: null,
    envelope: null,
    requestedEnvelope: requested,
  };
}

/** Carry the slot search's feasible start, rather than the shift's opening. */
export function withFeasibleStart(
  opportunity: StartOpportunity,
  dateStr: string,
  minute: number,
): StartOpportunity {
  return {
    ...opportunity,
    kind: dateStr === opportunity.requestedEnvelope.dateStr ? 'same_day' : 'future_day',
    startDateStr: dateStr,
    startMinute: minute,
  };
}

/**
 * A chronological sort key: days since epoch × 1440 + minute.
 *
 * NOT minutes-past-midnight. That is defect C — it discards the date, so
 * tomorrow 07:30 (450) sorted ahead of today 19:07 (1147) and the product
 * then described the later day as "earlier".
 *
 * Shop-local calendar arithmetic throughout, so a DST day changes the wall
 * clock without changing which day is first.
 */
export function earliestStartSortKey(o: StartOpportunity): number {
  if (o.startDateStr === null || o.startMinute === null) {
    // No opportunity sorts LAST. It must never win a tie-break.
    return Number.POSITIVE_INFINITY;
  }
  const [y, m, d] = o.startDateStr.split('-').map(Number);
  const dayNumber = Math.floor(Date.UTC(y, m - 1, d) / 86_400_000);
  return dayNumber * 1440 + o.startMinute;
}

/** Ascending: the genuinely earliest instant first. */
export function compareEarliestStart(a: StartOpportunity, b: StartOpportunity): number {
  return earliestStartSortKey(a) - earliestStartSortKey(b);
}

/** `yyyy-MM-dd` → `h:mm AM/PM`, for explanation text. */
function clockLabel(minute: number): string {
  const h24 = Math.floor(minute / 60);
  const mm = String(minute % 60).padStart(2, '0');
  const ampm = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${mm} ${ampm}`;
}

/**
 * Why `subject` sits where it does relative to `other`.
 *
 * The rule this exists to enforce: a candidate on a LATER date is never
 * described as starting "earlier". Floyd's read "they can start earlier (7:30
 * against 19:07)" about tomorrow morning versus this evening, and the number
 * was true only because the day had been thrown away.
 */
export function describeStartComparison(
  subject: StartOpportunity,
  other: StartOpportunity,
  _tz: string,
): string {
  if (subject.kind === 'none' || subject.startMinute === null) {
    return 'No working day was found for this technician.';
  }
  if (other.kind === 'none' || other.startMinute === null) {
    return `Can start ${clockLabel(subject.startMinute)}.`;
  }

  const sameDay = subject.startDateStr === other.startDateStr;
  if (sameDay) {
    return compareEarliestStart(subject, other) <= 0
      ? `Can start earlier (${clockLabel(subject.startMinute)} against ${clockLabel(other.startMinute)}).`
      : `Can start later (${clockLabel(subject.startMinute)} against ${clockLabel(other.startMinute)}).`;
  }

  // Different days. Say which, and never call the later one earlier.
  const subjectIsLater = compareEarliestStart(subject, other) > 0;
  return subjectIsLater
    ? `Not available until their next working day, ${subject.startDateStr} at `
      + `${clockLabel(subject.startMinute)} — later than ${clockLabel(other.startMinute)} `
      + `on ${other.startDateStr}.`
    : `Can start ${clockLabel(subject.startMinute)} on ${subject.startDateStr}, before `
      + `${clockLabel(other.startMinute)} on ${other.startDateStr}.`;
}
